export type SupportTicketReasonOption = {
  id: string;
  label: string;
  tag: string;
};

export type SupportTicketReasonCategory = {
  id: string;
  label: string;
  categoryTag?: string;
  options: SupportTicketReasonOption[];
};

export const supportTicketReasonCategories: SupportTicketReasonCategory[] = [
  {
    id: "logistyka",
    label: "Logistyka",
    categoryTag: "logistyka",
    options: [
      {
        id: "logistyka-uszkodzenia-w-transporcie",
        label: "Uszkodzenia w transporcie",
        tag: "uszkodzenia-w-transporcie",
      },
      { id: "logistyka-niekompletna-dostawa", label: "Niekompletna dostawa", tag: "niekompletna-dostawa" },
      { id: "logistyka-nieterminowa-dostawa", label: "Nieterminowa dostawa", tag: "nieterminowa-dostawa" },
      { id: "logistyka-inne", label: "Inne", tag: "logistyka-inne" },
    ],
  },
  {
    id: "opakowanie",
    label: "Opakowanie",
    categoryTag: "opakowanie",
    options: [
      { id: "opakowanie-pekajace-uchwyty", label: "Pękające uchwyty", tag: "pekajace-uchwyty" },
      { id: "opakowanie-zapadajace-wieczka", label: "Zapadające wieczka", tag: "zapadajace-wieczka" },
      { id: "opakowanie-brudne-pokrywki", label: "Brudne pokrywki", tag: "brudne-pokrywki" },
      { id: "opakowanie-inne", label: "Inne", tag: "opakowanie-inne" },
    ],
  },
  {
    id: "jakosc",
    label: "Jakość",
    categoryTag: "jakosc",
    options: [
      { id: "jakosc-konsystencja", label: "Konsystencja", tag: "konsystencja" },
      { id: "jakosc-aplikacja", label: "Aplikacja", tag: "aplikacja" },
      { id: "jakosc-grudki", label: "Grudki", tag: "grudki" },
      { id: "jakosc-wtracenia", label: "Wtrącenia", tag: "wtracenia" },
      { id: "jakosc-pekanie", label: "Pękanie", tag: "pekanie" },
      {
        id: "jakosc-bardzo-miekkie-podczas-szlifowania",
        label: "Bardzo miękki podczas szlifowania",
        tag: "bardzo-miekkie-podczas-szlifowania",
      },
      {
        id: "jakosc-bardzo-twarde-podczas-szlifowania",
        label: "Bardzo twardy podczas szlifowania",
        tag: "bardzo-twarde-podczas-szlifowania",
      },
    ],
  },
  {
    id: "specjalista",
    label: "Specjalista",
    categoryTag: "specjalista",
    options: [
      { id: "specjalista-nieaktywne-kody", label: "Nieaktywne kody", tag: "nieaktywne-kody" },
      { id: "specjalista-wadliwe-kody", label: "Wadliwe kody", tag: "wadliwe-kody" },
      { id: "specjalista-nagrody", label: "Nagrody", tag: "nagrody" },
      { id: "specjalista-aplikacja", label: "Aplikacja", tag: "aplikacja" },
    ],
  },
  {
    id: "obsluga",
    label: "Obsługa",
    options: [
      {
        id: "obsluga-niezgodnosc-towaru-z-zamowieniem",
        label: "Niezgodność towaru z zamówieniem",
        tag: "niezgodnosc-towaru-z-zamowieniem",
      },
      { id: "obsluga-inne", label: "Inne", tag: "obsluga-inne" },
    ],
  },
  {
    id: "wniosek-rozwojowy",
    label: "Wniosek rozwojowy",
    options: [
      {
        id: "wniosek-rozwojowy-opis",
        label: "Opis sugerowanych rozwiązań, porównanie do konkurencji",
        tag: "wniosek-rozwojowy",
      },
    ],
  },
];

function uniqueNormalizedTags(tags: string[]) {
  return tags
    .map((tag) => tag.trim().toLowerCase())
    .filter((tag, index, all) => tag.length > 0 && all.indexOf(tag) === index);
}

export function getAllSupportReasonTags() {
  const tags: string[] = [];
  for (const category of supportTicketReasonCategories) {
    if (category.categoryTag) tags.push(category.categoryTag);
    tags.push(category.id);
    for (const option of category.options) {
      tags.push(option.tag);
    }
  }
  return uniqueNormalizedTags(tags);
}

export function buildTagsFromSelectedReasons(selectedReasonIds: string[]) {
  const tags: string[] = [];
  for (const category of supportTicketReasonCategories) {
    const selectedInCategory = category.options.filter((option) => selectedReasonIds.includes(option.id));
    if (selectedInCategory.length === 0) continue;

    const categoryTag = category.categoryTag ?? category.id;
    const selectedSpecificOptions = selectedInCategory.filter((option) => !option.id.endsWith("-inne"));
    const isInneOnly = selectedSpecificOptions.length === 0;

    if (isInneOnly) {
      tags.push(categoryTag);
      continue;
    }

    if (category.categoryTag) tags.push(category.categoryTag);
    for (const option of selectedSpecificOptions) tags.push(option.tag);
  }
  return uniqueNormalizedTags(tags);
}

export function formatSelectedReasonsForMessage(selectedReasonIds: string[]) {
  const lines: string[] = [];
  for (const category of supportTicketReasonCategories) {
    const selectedLabels = category.options
      .filter((option) => selectedReasonIds.includes(option.id))
      .map((option) => option.label);
    if (selectedLabels.length === 0) continue;
    lines.push(`${category.label}: ${selectedLabels.join(", ")}`);
  }
  if (lines.length === 0) return "";
  return `\n\n---\nPrzedmiot zgłoszenia:\n${lines.join("\n")}`;
}

export function getSelectedReasonSummary(selectedReasonIds: string[]) {
  const labels: string[] = [];
  for (const category of supportTicketReasonCategories) {
    for (const option of category.options) {
      if (selectedReasonIds.includes(option.id)) labels.push(option.label);
    }
  }
  return labels.join(", ");
}

export type SupportCreateTicketContactOption = {
  label: string;
  value: string;
  email: string;
  name: string;
};

export function buildSupportCreateTicketContactOptions(
  hasCustomerUuid: boolean,
  customer: { email?: string | null; name?: string | null } | undefined,
  contacts: Array<{ email: string; name: string }> | undefined,
): SupportCreateTicketContactOption[] {
  if (!hasCustomerUuid) return [];
  const options: SupportCreateTicketContactOption[] = [];
  const customerEmail = customer?.email?.trim().toLowerCase() ?? "";
  const customerName = customer?.name?.trim() ?? "";
  if (customerEmail && customerName) {
    options.push({
      label: `${customerName} (${customerEmail})`,
      value: `${customerEmail}|${customerName}`,
      email: customerEmail,
      name: customerName,
    });
  }
  for (const contact of contacts ?? []) {
    const email = contact.email.trim().toLowerCase();
    const name = contact.name.trim();
    if (!email || !name) continue;
    options.push({ label: `${name} (${email})`, value: `${email}|${name}`, email, name });
  }
  const deduplicated: SupportCreateTicketContactOption[] = [];
  for (const option of options) {
    if (!deduplicated.some((item) => item.value === option.value)) deduplicated.push(option);
  }
  return deduplicated;
}
